import type {StatusBadgeConfigurationResponse} from '@github-ui/dotcom-schema/types/actions/v1/status_badge_configuration'
import {announce} from '@github-ui/aria-live'
import {useCopyToClipboard} from '@github-ui/copy-to-clipboard/useCopyToClipboard'
import {SoftNavOnlyLink as ReactLink} from '@github-ui/react-router'
import {RefSelector} from '@github-ui/ref-selector'
import {useAnalytics} from '@github-ui/use-analytics'
import {AlertIcon} from '@primer/octicons-react'
import {FormControl, Link as PrimerLink, Select, Spinner, Textarea} from '@primer/react'
import {Blankslate, Dialog} from '@primer/react/experimental'
import {useId, useRef, useState, type FocusEvent, type RefObject} from 'react'

import {useStatusBadgeConfiguration} from '../hooks/use-status-badge-configuration'
import styles from './StatusBadgeDialog.module.css'

interface StatusBadgeDialogProps {
  isOpen: boolean
  lab?: boolean
  onClose: () => void
  owner: string
  repo: string
  returnFocusRef?: RefObject<HTMLElement | null>
  workflowFilename: string
}

interface StatusBadgeValues {
  imageUrl: string
  markdown: string
  workflowPath: string
}

function selectAllMarkdown(event: FocusEvent<HTMLTextAreaElement>) {
  // This runs once on focus and intentionally matches the classic dialog's data-autoselect behavior.
  event.currentTarget.select()
}

function parseHttpUrl(value: string) {
  try {
    if (!URL.canParse(value)) return undefined
    const url = new URL(value, 'https://github.com')
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : undefined
  } catch {
    return undefined
  }
}

/** Derives the selected badge image URL, linked Markdown, and internal workflow path. */
export function buildStatusBadgeValues(
  configuration: StatusBadgeConfigurationResponse,
  branch: string,
  event: string,
): StatusBadgeValues | undefined {
  const badgeUrl = parseHttpUrl(configuration.badgeUrl)
  const workflowUrl = parseHttpUrl(configuration.workflowUrl)
  if (!badgeUrl || !workflowUrl) return undefined

  if (branch) badgeUrl.searchParams.set('branch', branch)
  else badgeUrl.searchParams.delete('branch')

  if (event) badgeUrl.searchParams.set('event', event)
  else badgeUrl.searchParams.delete('event')

  const imageUrl = badgeUrl.toString()
  const absoluteWorkflowUrl = workflowUrl.toString()
  const workflowPath = `${workflowUrl.pathname}${workflowUrl.search}${workflowUrl.hash}`
  const workflowName = configuration.workflowName.replaceAll('\\', '\\\\').replaceAll('[', '\\[').replaceAll(']', '\\]')

  return {
    imageUrl,
    markdown: `[![${workflowName}](${imageUrl})](${absoluteWorkflowUrl})`,
    workflowPath,
  }
}

function StatusBadgeConfigurationContent({
  configuration,
  owner,
  repo,
  selectedBranch,
  selectedEvent,
  values,
  onSelectBranch,
  onSelectEvent,
}: {
  configuration: StatusBadgeConfigurationResponse
  owner: string
  repo: string
  selectedBranch: string
  selectedEvent: string
  values?: StatusBadgeValues
  onSelectBranch: (branch: string) => void
  onSelectEvent: (event: string) => void
}) {
  const branchSelectorId = useId()
  const branchSelectorButtonId = `ref-picker-${branchSelectorId}`
  const markdownId = useId()
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  const sendInteraction = (action: string, label: string) => {
    sendAnalyticsEvent('analytics.click', undefined, {
      category: 'ACTIONS_WORKFLOWS',
      action,
      label,
    })
  }

  if (!values) {
    return (
      <Blankslate narrow>
        <Blankslate.Visual>
          <AlertIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>Status badge configuration could not be loaded.</Blankslate.Heading>
        <Blankslate.Description>The status badge URLs returned by the server were invalid.</Blankslate.Description>
      </Blankslate>
    )
  }

  const branchLabel = selectedBranch || 'Default branch'

  return (
    <div className={styles.Content}>
      <div className={styles.BadgePreview}>
        <PrimerLink as={ReactLink} to={values.workflowPath}>
          <img
            className={styles.Badge}
            src={values.imageUrl}
            alt={`${configuration.workflowName} workflow status badge`}
            height={20}
          />
        </PrimerLink>
      </div>

      <FormControl id={branchSelectorButtonId}>
        <FormControl.Label>Branch</FormControl.Label>
        <RefSelector
          ariaLabel={`Branch: ${branchLabel}`}
          buttonPrefix="Branch"
          buttonText={branchLabel}
          cacheKey=""
          canCreate={false}
          closeOnSelect
          currentCommitish={selectedBranch || configuration.defaultBranch}
          customFooterItemProps={{
            text: 'Use default branch',
            onClick: () => {
              onSelectBranch('')
              sendInteraction('status_badge.select_default_branch', 'ref_loc:status_badge_dialog')
            },
          }}
          defaultBranch={configuration.defaultBranch}
          hideShowAll
          idEnding={branchSelectorId}
          onOpenChange={open => {
            if (open) sendInteraction('status_badge.open_branch_selector', 'ref_loc:status_badge_dialog')
          }}
          onSelectItem={branch => {
            onSelectBranch(branch)
            sendInteraction('status_badge.select_branch', 'ref_loc:status_badge_dialog')
          }}
          owner={owner}
          persistButtonText
          repo={repo}
          selectedRefType="branch"
          types={['branch']}
        />
      </FormControl>

      <FormControl>
        <FormControl.Label>Event</FormControl.Label>
        <Select
          block
          value={selectedEvent}
          onChange={event => {
            onSelectEvent(event.currentTarget.value)
            sendInteraction('status_badge.select_event', 'ref_loc:status_badge_dialog')
          }}
        >
          <Select.Option value="">Default</Select.Option>
          {configuration.events.map(event => (
            <Select.Option key={event} value={event}>
              {event}
            </Select.Option>
          ))}
        </Select>
      </FormControl>

      <FormControl id={markdownId} className={styles.MarkdownControl}>
        <FormControl.Label>Markdown</FormControl.Label>
        <Textarea
          className={styles.Output}
          readOnly
          value={values.markdown}
          rows={4}
          resize="none"
          block
          onFocus={selectAllMarkdown}
        />
      </FormControl>
    </div>
  )
}

/** Renders the lazy workflow status badge builder in a centered Primer dialog. */
export function StatusBadgeDialog({
  isOpen,
  lab = false,
  onClose,
  owner,
  repo,
  returnFocusRef,
  workflowFilename,
}: StatusBadgeDialogProps) {
  const configurationQuery = useStatusBadgeConfiguration({
    owner,
    repo,
    workflowFilename,
    lab,
    enabled: isOpen,
  })
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const contentStatusRef = useRef<HTMLDivElement>(null)
  const [selectedBranch, setSelectedBranch] = useState('')
  const [selectedEvent, setSelectedEvent] = useState('')
  const values = configurationQuery.data
    ? buildStatusBadgeValues(configurationQuery.data, selectedBranch, selectedEvent)
    : undefined
  const {copied, copyToClipboard, visual} = useCopyToClipboard({
    textToCopy: values?.markdown ?? '',
    onCopy: () =>
      sendAnalyticsEvent('analytics.click', undefined, {
        category: 'ACTIONS_WORKFLOWS',
        action: 'status_badge.copy_markdown',
        label: 'ref_loc:status_badge_dialog',
      }),
  })

  if (!isOpen) return null

  const sendInteraction = (action: string, label: string) => {
    sendAnalyticsEvent('analytics.click', undefined, {category: 'ACTIONS_WORKFLOWS', action, label})
  }
  const handleClose = () => {
    setSelectedBranch('')
    setSelectedEvent('')
    sendInteraction('status_badge.close', 'ref_loc:status_badge_dialog')
    onClose()
  }
  const handleCopy = () => {
    copyToClipboard()
    announce('Copied!')
  }

  return (
    <Dialog
      title="Create status badge"
      width="large"
      height="auto"
      returnFocusRef={returnFocusRef}
      onClose={handleClose}
      footerButtons={
        values
          ? [
              {
                content: copied ? 'Copied' : 'Copy status badge Markdown',
                buttonType: 'primary',
                leadingVisual: visual,
                block: true,
                onClick: handleCopy,
              },
            ]
          : undefined
      }
    >
      <div ref={contentStatusRef} role="region" aria-label="Status badge configuration" tabIndex={-1}>
        {configurationQuery.isPending ? (
          <div className={styles.Loading}>
            <Spinner srText="Loading status badge configuration" />
          </div>
        ) : configurationQuery.isError || !configurationQuery.data ? (
          <Blankslate narrow>
            <Blankslate.Visual>
              <AlertIcon size="medium" />
            </Blankslate.Visual>
            <Blankslate.Heading>Status badge configuration could not be loaded.</Blankslate.Heading>
            <Blankslate.Description>Try loading the status badge configuration again.</Blankslate.Description>
            <Blankslate.PrimaryAction
              onClick={() => {
                queueMicrotask(() => {
                  // This runs once after Retry so focus is not stranded on <body> when the button unmounts.
                  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-input-handler -- discrete a11y focus restore
                  contentStatusRef.current?.focus()
                })
                sendInteraction('status_badge.retry', 'ref_loc:status_badge_dialog')
                void configurationQuery.refetch()
              }}
            >
              Retry
            </Blankslate.PrimaryAction>
          </Blankslate>
        ) : (
          <StatusBadgeConfigurationContent
            configuration={configurationQuery.data}
            owner={owner}
            repo={repo}
            selectedBranch={selectedBranch}
            selectedEvent={selectedEvent}
            values={values}
            onSelectBranch={setSelectedBranch}
            onSelectEvent={setSelectedEvent}
          />
        )}
      </div>
    </Dialog>
  )
}
